import {
    Table,
    TableBody,
    TableCell,
    TableHeader,
    TableRow,
} from "../ui/table";

import Badge from "../ui/badge/Badge";
import { useNavigate } from "react-router";
import Pagination from "../ui/pagination/Pagination";

// Interface para os dados da API
interface SaleItem {
    id: string;                // ID do pagamento
    transactionId: string;     // Referência externa
    date: string;              // Data da criação
    paidAt: string | null;     // Data do pagamento
    customer: {
        id: string;
        name: string;
        email: string;
        document: string;
    };
    order: {
        id: string;
        code: string;
        totalAmount: number;
    };
    item: {
        id: string;
        productName: string;
        offerName: string;
        price: number;
        quantity: number;
    };
    payment: {
        method: string;
        grossAmount: number;
        netAmount: number;
        status: string;
        saleParticipantGrossAmount?: number;
    };
}


interface SalesTableProps {
    salesList?: SaleItem[];
    isLoading?: boolean;
    // Props de paginação do backend
    currentPage: number;
    totalPages: number;
    onPageChange: (page: number) => void;
}

// Mapeamento de status para cores de badge
const getStatusBadgeColor = (status: string) => {
    switch (status.toLowerCase()) {
        case 'approved':
        case 'paid':
            return 'success';
        case 'pending':
        case 'pending_payment':
            return 'warning';
        case 'refused':
        case 'canceled':
        case 'cancelled':
        case 'failed':
        case 'chargeback':
        case 'declined':
            return 'error';
        case 'refunded':
            return 'info';
        case 'overdue':
        case 'expired':
            return 'warning';
        default:
            return 'warning';
    }
};

// Mapeamento de status para textos amigáveis
const getStatusDisplayText = (status: string) => {
    switch (status.toLowerCase()) {
        case 'approved':
        case 'paid':
            return 'Paga';
        case 'pending':
        case 'pending_payment':
            return 'Pendente';
        case 'refused':
        case 'declined':
            return 'Recusada';
        case 'canceled':
        case 'cancelled':
            return 'Cancelada';
        case 'failed':
            return 'Falha';
        case 'refunded':
            return 'Reembolsada';
        case 'chargeback':
            return 'Chargeback';
        case 'overdue':
            return 'Vencida';
        case 'expired':
            return 'Expirada';
        default:
            return status;
    }
};

// Formatar valor monetário
const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('pt-BR', {
        style: 'currency',
        currency: 'BRL'
    }).format(value);
};

// Formatar data
const formatDate = (dateString: string | null) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    return new Intl.DateTimeFormat('pt-BR', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
    }).format(date);
};

export default function SalesTable({
    salesList = [],
    isLoading = false,
    currentPage,
    totalPages,
    onPageChange
}: SalesTableProps) {
    const navigate = useNavigate();

    // Navegação para a página de detalhes
    const handleRowClick = (id: string) => {
        navigate(`/sales/${id}`);
    };

    return (
        <div className="overflow-hidden rounded-xl border border-gray-200 bg-white dark:border-white/[0.05] dark:bg-white/[0.03]">
            <div className="max-w-full overflow-x-auto">
                <Table>
                    {/* Table Header */}
                    <TableHeader className="border-b border-gray-100 dark:border-white/[0.05]">
                        <TableRow>
                            <TableCell
                                isHeader
                                className="px-5 py-3 font-medium text-gray-500 text-start text-theme-xs dark:text-gray-400"
                            >
                                Cliente
                            </TableCell>
                            <TableCell
                                isHeader
                                className="px-5 py-3 font-medium text-gray-500 text-start text-theme-xs dark:text-gray-400"
                            >
                                Forma de Pagamento
                            </TableCell>
                            <TableCell
                                isHeader
                                className="px-5 py-3 font-medium text-gray-500 text-start text-theme-xs dark:text-gray-400"
                            >
                                Status
                            </TableCell>
                            <TableCell
                                isHeader
                                className="px-5 py-3 font-medium text-gray-500 text-start text-theme-xs dark:text-gray-400"
                            >
                                Produto
                            </TableCell>
                            <TableCell
                                isHeader
                                className="px-5 py-3 font-medium text-gray-500 text-start text-theme-xs dark:text-gray-400"
                            >
                                Data da Transação
                            </TableCell>
                            <TableCell
                                isHeader
                                className="px-5 py-3 font-medium text-gray-500 text-start text-theme-xs dark:text-gray-400"
                            >
                                Data do Pagamento
                            </TableCell>
                        </TableRow>
                    </TableHeader>

                    {/* Table Body */}
                    <TableBody className="divide-y divide-gray-100 dark:divide-white/[0.05]">
                        {isLoading ? (
                            // Estado de carregamento
                            Array(5).fill(0).map((_, index) => (
                                <TableRow key={`loading-${index}`}>
                                    {Array(6).fill(0).map((_, cellIndex) => (
                                        <TableCell key={`loading-cell-${cellIndex}`} className="px-5 py-4">
                                            <div className="h-5 bg-gray-200 animate-pulse rounded dark:bg-gray-700"></div>
                                        </TableCell>
                                    ))}
                                </TableRow>
                            ))
                        ) : salesList.length === 0 ? (
                            // Mensagem quando não há dados
                            <TableRow>
                                <TableCell className="px-5 py-8 text-center text-gray-500 dark:text-gray-400" colSpan={6}>
                                    <div className="flex justify-center items-center w-full">
                                        <div className="flex flex-col items-center justify-center py-10">
                                            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="lucide lucide-search-x mb-3 size-10 text-gray-400 dark:text-gray-600">
                                                <path d="M10 10m-7 0a7 7 0 1 0 14 0a7 7 0 1 0 -14 0"></path>
                                                <path d="M21 21l-6-6"></path>
                                                <path d="M10 7v6"></path>
                                                <path d="M7 10h6"></path>
                                            </svg>
                                            <p className="text-lg font-medium">Nenhuma venda encontrada</p>
                                            <p className="text-sm text-gray-500 dark:text-gray-400">Tente ajustar os filtros ou a pesquisa</p>
                                        </div>
                                    </div>
                                </TableCell>
                            </TableRow>
                        ) : (
                            // Renderização dos dados (já vem paginado do backend)
                            salesList.map((sale) => (
                                <TableRow
                                    key={sale.id}
                                    onClick={() => handleRowClick(sale.id)}
                                    className="cursor-pointer hover:bg-gray-50 dark:hover:bg-white/[0.02] transition-colors"
                                >
                                    <TableCell className="px-5 py-4 text-start">
                                        <div className="flex text-xs truncate overflow-x-hidden text-[#232323] dark:text-[#797979] lg:!flex-[1.2]">
                                            <div className="flex flex-col w-full lg:dark:border-neutral-900 border-neutral-200">
                                                <div className="flex items-center gap-4">
                                                    <div className="size-9 flex items-center justify-center  dark:bg-[#0F0F0F] bg-neutral-200 border dark:border-neutral-800 border-neutral-200  rounded-full">
                                                        <p className="text-md dark:text-white  text-black font-bold uppercase p-1">{sale.customer.name.charAt(0).toUpperCase()}</p>
                                                    </div>
                                                    <div className="flex-col hidden lg:flex">
                                                        <p className="text-black dark:text-white font-medium text-sm ">{sale.customer.name}</p>
                                                        <p className="text-xs">{sale.customer.email}</p>
                                                    </div>
                                                    <div className="flex-col flex lg:hidden">
                                                        <p className="text-black text-wrap text-left line-clamp-2 dark:text-white font-medium text-sm ">{sale.customer.name}</p>
                                                        <p className="text-xs">{sale.customer.email}</p>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </TableCell>
                                    <TableCell className="px-5 py-4 text-gray-500 text-start text-theme-sm dark:text-gray-400">
                                        <div className="flex items-center gap-2">
                                            <div className="border bg-transparent dark:border-neutral-700 border-neutral-300 rounded-lg size-8 flex items-center justify-center">
                                                {sale.payment.method.toLowerCase() === 'boleto' && (
                                                    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="lucide lucide-barcode size-5"><path d="M3 5v14"></path><path d="M8 5v14"></path><path d="M12 5v14"></path><path d="M17 5v14"></path><path d="M21 5v14"></path></svg>
                                                )}
                                                {(sale.payment.method.toLowerCase() === 'pix' || sale.payment.method.toLowerCase() === 'qrcode') && (
                                                    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="lucide lucide-qr-code size-5"><rect width="5" height="5" x="3" y="3" rx="1"></rect><rect width="5" height="5" x="16" y="3" rx="1"></rect><rect width="5" height="5" x="3" y="16" rx="1"></rect><path d="M21 16h-3a2 2 0 0 0-2 2v3"></path><path d="M21 21v.01"></path><path d="M12 7v3a2 2 0 0 1-2 2H7"></path><path d="M3 12h.01"></path><path d="M12 3h.01"></path><path d="M12 16v.01"></path><path d="M16 12h1"></path><path d="M21 12v.01"></path><path d="M12 21v-1"></path></svg>
                                                )}
                                                {(sale.payment.method.toLowerCase() === 'credit_card' || sale.payment.method.toLowerCase() === 'card' || sale.payment.method.toLowerCase() === 'cartao' || sale.payment.method.toLowerCase() === 'cartão') && (
                                                    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="lucide lucide-credit-card size-5"><rect width="20" height="14" x="2" y="5" rx="2"></rect><line x1="2" x2="22" y1="10" y2="10"></line></svg>
                                                )}
                                                {/* Caso nenhum método corresponda aos acima */}
                                                {!['boleto', 'pix', 'qrcode', 'credit_card', 'card', 'cartao', 'cartão'].includes(sale.payment.method.toLowerCase()) && (
                                                    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="lucide lucide-credit-card size-5"><rect width="20" height="14" x="2" y="5" rx="2"></rect><line x1="2" x2="22" y1="10" y2="10"></line></svg>
                                                )}
                                            </div>
                                            <div className="flex flex-col">
                                                {formatCurrency(
                                                    sale.payment.saleParticipantGrossAmount ?? sale.payment.grossAmount
                                                )}
                                            </div>
                                        </div>
                                    </TableCell>
                                    <TableCell className="px-5 py-4 text-gray-500 text-start text-theme-sm dark:text-gray-400">
                                        <Badge
                                            size="sm"
                                            color={getStatusBadgeColor(sale.payment.status)}
                                        >
                                            {getStatusDisplayText(sale.payment.status)}
                                        </Badge>
                                    </TableCell>
                                    <TableCell className="px-5 py-4 text-gray-500 text-start text-theme-sm dark:text-gray-400">
                                        {sale.item?.productName
                                            ? (sale.item.productName.length > 20
                                                ? `${sale.item.productName.substring(0, 20)}...`
                                                : sale.item.productName)
                                            : 'Produto indisponível'}
                                    </TableCell>
                                    <TableCell className="px-5 py-4 text-gray-500 text-start text-theme-sm dark:text-gray-400">
                                        {formatDate(sale.date)}
                                    </TableCell>

                                    <TableCell className="px-5 py-4 text-gray-500 text-start text-theme-sm dark:text-gray-400">
                                        {formatDate(sale.paidAt)}
                                    </TableCell>
                                </TableRow>
                            ))
                        )}
                    </TableBody>
                </Table>
                {totalPages > 1 && (
                    <div className="p-4 border-t border-gray-100 dark:border-white/[0.05]">
                        <Pagination
                            currentPage={currentPage}
                            totalPages={totalPages}
                            onPageChange={onPageChange}
                        />
                    </div>
                )}
            </div>
        </div>
    );
}